import { useAuth } from "context/AuthContext";
import dayjs from "dayjs";
import { useRefreshToken } from "hooks/api/useAuth";
import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { retrieveToken, storeToken } from "utils/storage";

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const refreshTokenMutation = useRefreshToken();
  const debounceRef = useRef(null);
  const location = useLocation();
  const isRefreshing = useRef(false);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      if (isRefreshing.current) return;

      const token = retrieveToken("accessToken");
      const refreshToken = retrieveToken("refreshToken");
      const expiry = retrieveToken("tokenExpiryTime");

      if (!token) {
        logout();
        return;
      }

      const expiresAt = dayjs(expiry);
      const now = dayjs();
      const timeLeft = expiresAt?.diff(now, "milliseconds");

      if (timeLeft <= 10 * 60 * 1000 && refreshToken) {
        try {
          isRefreshing.current = true;
          const response = await refreshTokenMutation.mutateAsync({
            refresh: refreshToken,
          });

          if (response?.data?.access_expires) {
            storeToken("accessToken", response?.data.access);
            const expiry = dayjs
              .unix(response?.data?.access_expires)
              .toISOString();
            storeToken("tokenExpiryTime", expiry);
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
          logout();
          return;
        } finally {
          isRefreshing.current = false;
        }
      }
    };

    checkAndRefreshToken();

    const debounceCheck = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(checkAndRefreshToken, 1500);
    };

    const events = ["click", "mousemove", "keydown", "scroll"];
    events.forEach((evt) => document.addEventListener(evt, debounceCheck));
    window.addEventListener("focus", debounceCheck);

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, debounceCheck));
      window.removeEventListener("focus", debounceCheck);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

export default PrivateRoute;
